/// <amd-module name="Artifacts/Package/redux/Actions" />
import { IProtocol } from "Artifacts/Root/protocols/types";
import { ILoadFailedPayload } from "Artifacts/Root/Redux";
import { IFeed, IPackage, IPackageVersion, IPackageVersionIdentity } from "Artifacts/Root/types";
import { SkeletalPackageVersion } from "Artifacts/Root/types/AzureArtifacts";
import { UpstreamVersionsData } from "Artifacts/Root/types/PackagingShared";
import {
  ActionsUnion,
  createAction,
  trackedAction,
  trackedErrorAction,
} from "redux/Utils/ActionHelper";
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

export const enum PackageActionTypes {
  LoadPackageDetails = "Artifacts/Package/LoadPackageDetails",

  LoadPackageDetailsCompleted = "Artifacts/Package/LoadPackageDetailsCompleted",
  LoadPackageDetailsFailed = "Artifacts/Package/LoadPackageDetailsFailed",

  LoadUpstreamVersions = "Artifacts/Package/LoadUpstreamVersions",
  LoadUpstreamVersionsCompleted = "Artifacts/Package/LoadUpstreamVersionsCompleted",
  LoadUpstreamVersionsFailed = "Artifacts/Package/LoadUpstreamVersionsFailed",

  GetFollowState = "Artifacts/Package/GetFollowState",
  GetFollowStateCompleted = "Artifacts/Package/GetFollowStateCompleted",

  SetFollowState = "Artifacts/Package/SetFollowState",
  SetFollowStateCompleted = "Artifacts/Package/SetFollowStateCompleted",

  FakePackagePromote = "Artifacts/Package/FakePackagePromote",
  FakePackageDelete = "Artifacts/Package/FakePackageDelete",
  FakePackageListUnlist = "Artifacts/Package/FakePackageListUnlist",
  FakePackageDeprecate = "Artifacts/Package/FakePackageDeprecate",

  UpstreamOverrideCommandStarted = "Artifacts/Package/UpstreamOverrideCommandStarted",
  UpstreamOverrideCommandCompleted = "Artifacts/Package/UpstreamOverrideCommandCompleted",
  UpstreamOverrideCommandFailed = "Artifacts/Package/UpstreamOverrideCommandFailed",

  UpstreamOverrideModalStarted = "Artifacts/Package/UpstreamOverrideModalStarted",
  UpstreamOverrideModalCompleted = "Artifacts/Package/UpstreamOverrideModalCompleted",
  UpstreamOverrideModalFailed = "Artifacts/Package/UpstreamOverrideModalFailed",

  IngestFromUpstream = "Artifacts/Package/IngestFromUpstream",
  IngestFromUpstreamStarted = "Artifacts/Package/IngestFromUpstreamStarted",
  IngestFromUpstreamCompleted = "Artifacts/Package/IngestFromUpstreamCompleted",
  IngestFromUpstreamFailed = "Artifacts/Package/IngestFromUpstreamFailed",
}

export interface ILoadPackageDetailsPayload {
  feedId: string;
  protocol: string;
  pkgName: string;
  onSuccessCallback: (packageProtocol: string, packageName: string, packageVersion: string) => void;
  version?: string;
  projectName?: string;
  viewId?: string;
  preferRelease?: boolean;
}

export interface IIngestFromUpstreamPayload {
  onSuccess: () => void;
  feedId: string;
  protocolName: string;
  pkgName: string;
  version?: string;
  projectName?: string;
  viewId?: string;
}

export interface ILoadUpstreamVersionsPayload {
  feedId: string;
  protocol: string;
  pkgName: string;
  projectId?: string;
}

export interface ILoadPackageDetailsCompletedPayload {
  pkg: IPackage;
  currentPackageVersion: IPackageVersion;
  versions: SkeletalPackageVersion[];
}

export interface ILoadUpstreamVersionsCompletedPayload {
  upstreamVersionsData: UpstreamVersionsData;
}

export interface IIngestFromUpstreamCompletedPayload {
  ingestedNormalizedVersion: string;
}

export interface IFollowActionPayload {
  feed: IFeed;
  packageToFollow: IPackage;
  state?: boolean;
}

export interface IFollowActionCompletedPayload {
  followingState: boolean;
}

export interface IFakePromotePackagePayload {
  selectedVersions: IPackageVersionIdentity[];
  viewName: string;
}

export interface IFakeDeletePackagePayload {
  selectedVersions: IPackageVersionIdentity[];
}

export interface IFakeListUnlistPackagePayload {
  selectedVersions: IPackageVersionIdentity[];
  isListed: boolean;
}

export interface IFakeDeprecatePackagePayload {
  selectedVersions: IPackageVersionIdentity[];
  deprecateMessage: string;
}

export interface ICommandFailedPayload {
  error: string;
}

export interface IUpstreamOverrideModalStartPayload {
  protocol: IProtocol;
  packageName: string;
}

export const PackageActions = {
  loadPackageDetails: (
    feedId: string,
    protocol: string,
    pkgName: string,
    onSuccessCallback: (
      packageProtocol: string,
      packageName: string,
      packageVersion: string,
    ) => void,
    version?: string,
    viewId?: string,
    projectName?: string,
    preferRelease?: boolean,
  ) =>
    createAction<PackageActionTypes, ILoadPackageDetailsPayload>(
      PackageActionTypes.LoadPackageDetails,
      {
        feedId,
        protocol,
        pkgName,
        onSuccessCallback,
        version,
        viewId,
        projectName,
        preferRelease,
      },
    ),
  loadPackageDetailsCompleted: (
    pkg: IPackage,
    currentPackageVersion: IPackageVersion,
    versions: SkeletalPackageVersion[],
  ) =>
    createAction<PackageActionTypes, ILoadPackageDetailsCompletedPayload>(
      PackageActionTypes.LoadPackageDetailsCompleted,
      {
        pkg,
        currentPackageVersion,
        versions,
      },
    ),
  loadPackageDetailsFailed: (error: string) =>
    createAction<PackageActionTypes, ILoadFailedPayload>(
      PackageActionTypes.LoadPackageDetailsFailed,
      {
        error,
      },
    ),
  loadUpstreamVersions: (feedId: string, protocol: string, pkgName: string, projectId?: string) =>
    createAction<PackageActionTypes, ILoadUpstreamVersionsPayload>(
      PackageActionTypes.LoadUpstreamVersions,
      {
        feedId,
        protocol,
        pkgName,
        projectId,
      },
    ),
  loadUpstreamVersionsCompleted: (upstreamVersionsData: UpstreamVersionsData) =>
    createAction<PackageActionTypes, ILoadUpstreamVersionsCompletedPayload>(
      PackageActionTypes.LoadUpstreamVersionsCompleted,
      {
        upstreamVersionsData,
      },
    ),
  loadUpstreamVersionsFailed: (error: string) =>
    createAction<PackageActionTypes, ILoadFailedPayload>(
      PackageActionTypes.LoadUpstreamVersionsFailed,
      {
        error,
      },
    ),
  getFollowState: (feed: IFeed, packageToFollow: IPackage) =>
    createAction<PackageActionTypes, IFollowActionPayload>(PackageActionTypes.GetFollowState, {
      feed,
      packageToFollow,
    }),
  getFollowStateCompleted: (followingState: boolean) =>
    createAction<PackageActionTypes, IFollowActionCompletedPayload>(
      PackageActionTypes.GetFollowStateCompleted,
      { followingState },
    ),
  setFollowState: (feed: IFeed, packageToFollow: IPackage, state: boolean) =>
    trackedAction(
      createAction<PackageActionTypes, IFollowActionPayload>(PackageActionTypes.SetFollowState, {
        feed,
        packageToFollow,
        state,
      }),
    ),
  setFollowStateCompleted: (followingState: boolean) =>
    trackedAction(
      createAction<PackageActionTypes, IFollowActionCompletedPayload>(
        PackageActionTypes.SetFollowStateCompleted,
        { followingState },
      ),
    ),
  fakePackagePromote: (selectedVersions: IPackageVersionIdentity[], viewName: string) =>
    createAction<PackageActionTypes, IFakePromotePackagePayload>(
      PackageActionTypes.FakePackagePromote,
      {
        selectedVersions,
        viewName,
      },
    ),
  fakePackageDelete: (selectedVersions: IPackageVersionIdentity[]) =>
    createAction<PackageActionTypes, IFakeDeletePackagePayload>(
      PackageActionTypes.FakePackageDelete,
      {
        selectedVersions,
      },
    ),
  fakePackageListUnlist: (selectedVersions: IPackageVersionIdentity[], isListed: boolean) =>
    createAction<PackageActionTypes, IFakeListUnlistPackagePayload>(
      PackageActionTypes.FakePackageListUnlist,
      {
        selectedVersions,
        isListed,
      },
    ),
  fakePackageDeprecate: (selectedVersions: IPackageVersionIdentity[], deprecateMessage: string) =>
    createAction<PackageActionTypes, IFakeDeprecatePackagePayload>(
      PackageActionTypes.FakePackageDeprecate,
      {
        selectedVersions,
        deprecateMessage,
      },
    ),
  // not tracking here since we already have one to signal the start. Used only for component state.
  upstreamOverrideCommandStarted: () =>
    createAction(PackageActionTypes.UpstreamOverrideCommandStarted),
  upstreamOverrideCommandCompleted: (overrideStatus: boolean) =>
    trackedAction(
      createAction<PackageActionTypes, boolean>(
        PackageActionTypes.UpstreamOverrideCommandCompleted,
        overrideStatus,
      ),
    ),
  upstreamOverrideCommandFailed: (error: string) =>
    trackedErrorAction(
      createAction<PackageActionTypes, ICommandFailedPayload>(
        PackageActionTypes.UpstreamOverrideCommandFailed,
        {
          error,
        },
      ),
    ),
  upstreamOverrideModalStarted: (protocol: IProtocol, packageName: string) =>
    trackedAction(
      createAction<PackageActionTypes, IUpstreamOverrideModalStartPayload>(
        PackageActionTypes.UpstreamOverrideModalStarted,
        {
          protocol,
          packageName,
        },
      ),
    ),
  upstreamOverrideModalCompleted: (overrideStatus: boolean) =>
    trackedAction(
      createAction<PackageActionTypes, boolean>(
        PackageActionTypes.UpstreamOverrideModalCompleted,
        overrideStatus,
      ),
    ),
  upstreamOverrideModalFailed: (error: string) =>
    trackedErrorAction(
      createAction<PackageActionTypes, ICommandFailedPayload>(
        PackageActionTypes.UpstreamOverrideModalFailed,
        {
          error,
        },
      ),
    ),

  ingestFromUpstream: (
    onSuccess: () => void,
    feedId: string,
    protocolName: string,
    pkgName: string,
    version?: string,
    projectName?: string,
    viewId?: string,
  ) =>
    createAction<PackageActionTypes, IIngestFromUpstreamPayload>(
      PackageActionTypes.IngestFromUpstream,
      {
        onSuccess,
        feedId,
        protocolName,
        pkgName,
        version,
        projectName,
        viewId,
      },
    ),

  upstreamIngestionStarted: () => createAction(PackageActionTypes.IngestFromUpstreamStarted, {}),
  upstreamIngestionCompleted: (ingestedNormalizedVersion: string | undefined) =>
    createAction(PackageActionTypes.IngestFromUpstreamCompleted, {
      ingestedNormalizedVersion,
    }),
  upstreamIngestionFailed: (error: string) =>
    createAction<PackageActionTypes, ILoadFailedPayload>(
      PackageActionTypes.IngestFromUpstreamFailed,
      {
        error,
      },
    ),
};

export type PackageActions = ActionsUnion<typeof PackageActions>;
